import { formatDate } from "@/composables/formatUtils";
import type { SalesTransaction } from "@/types";
import { deadlineMonth } from "@/utils/primevueFilters";
import { compact, sortBy, uniq } from "lodash-es";
import type { RouteLocationRaw } from "vue-router";

// --- Forward delivery deadlines -----------------------------------------------------------------

/**
 * Options for the deadline-month filter (`DEADLINE_MONTH_MATCH_MODE`): every month a listed
 * delivery falls due in, soonest first, labelled `MM/YYYY`. Spot rows (no delivery) contribute
 * nothing.
 */
export function deadlineMonthOptions(
  transactions: SalesTransaction[]
): { label: string; value: string }[] {
  const months = uniq(
    compact(transactions.map((t) => t.forward_delivery?.window_end)).map(
      deadlineMonth
    )
  );
  return sortBy(months).map((month) => ({
    label: formatDate(month),
    value: month,
  }));
}

// --- Spot / Forwards views of the admin transactions list ---------------------------------------

/** The two admin transaction views: spot sales of existing inventory, and forwards v2 deliveries. */
export type TransactionsView = "spot" | "forwards";

export const TRANSACTIONS_VIEWS: TransactionsView[] = ["spot", "forwards"];

/** Spot is the default and the unqualified URL: most of the manual work happens there. */
export const DEFAULT_TRANSACTIONS_VIEW: TransactionsView = "spot";

export const TRANSACTIONS_VIEW_QUERY_KEY = "view";

/** Copy per view: the toggle label, the page heading, and the noun the empty states use. */
export const TRANSACTIONS_VIEW_COPY: Record<
  TransactionsView,
  { label: string; heading: string; noun: string }
> = {
  spot: { label: "Spot", heading: "Spot", noun: "spot" },
  forwards: { label: "Forwards", heading: "Forward", noun: "forward" },
};

/**
 * A forwards v2 delivery carries its deal header; a spot sale has none. Same rule the edit page
 * uses to lock the deal-owned fields, so a transaction lands in the same view it is edited as.
 */
export function isForwardTransaction(transaction: SalesTransaction): boolean {
  return transaction.forward_delivery != null;
}

/** The view a transaction lands in on the list page. */
export function transactionView(
  transaction: SalesTransaction
): TransactionsView {
  return isForwardTransaction(transaction) ? "forwards" : "spot";
}

/** The list page on the given view; the default view is the unqualified URL. */
export function transactionsListRoute(
  view: TransactionsView = DEFAULT_TRANSACTIONS_VIEW
): RouteLocationRaw {
  return {
    name: "AdminTransactionsListView",
    query:
      view === DEFAULT_TRANSACTIONS_VIEW
        ? {}
        : { [TRANSACTIONS_VIEW_QUERY_KEY]: view },
  };
}

export interface InvoiceModalPrefill {
  preFillDescription: string;
  preFillAmount: number;
  preFillUnitPrice: number;
  preFillBuyerName: string;
  preFillBuyerAddress: string;
  preFillBuyerCountry: string;
  preFillBuyerRegistryCode: string;
  preFillBuyerVat: string;
  preFillBuyerEmails: string;
}

/**
 * An invoice exists for the transaction but has not been registered with
 * Merit Aktiva yet — an outstanding admin to-do.
 */
export function needsMeritRegistration(transaction: SalesTransaction): boolean {
  return (
    transaction.buyer_sales_invoice_id !== null &&
    !transaction.buyer_sales_invoice_merit_registered
  );
}

/**
 * The buyer invoice exists and has not reached the buyer yet, so the send / mark-as-sent
 * actions apply. Sending is gated on DELIVERED (the buyer has the certificates); creating the
 * invoice itself is allowed earlier.
 *
 * `buyer_sales_invoice_email_sent` is set by the backend for manual sends too (the name
 * predates them); `buyer_sales_invoice_sent_at` is checked as well so this stays correct
 * even if that ever changes.
 */
export function canSendBuyerInvoice(transaction: SalesTransaction): boolean {
  return (
    transaction.status === "DELIVERED" &&
    transaction.buyer_sales_invoice_id !== null &&
    !transaction.buyer_sales_invoice_email_sent &&
    transaction.buyer_sales_invoice_sent_at === null
  );
}

/**
 * Build invoice modal prefill props from a sales transaction.
 *
 * Snapshot fields (`counterparty_name`/`counterparty_country`/`counterparty_emails`)
 * come from the transaction itself — that's the trade-time truth. Address/VAT/registry
 * code come from the linked `global_company` (read live) so write-back enrichment shows
 * up on every future invoice for that counterparty.
 */
export function buildInvoiceModalPrefill(
  transaction: SalesTransaction | null | undefined
): InvoiceModalPrefill {
  const gc = transaction?.global_company ?? null;
  const parsedUnitPrice = Number.parseFloat(transaction?.unit_price ?? "0.0");
  return {
    preFillDescription: transaction?.description ?? "",
    preFillAmount: transaction?.amount ?? 1,
    preFillUnitPrice: Number.isFinite(parsedUnitPrice) ? parsedUnitPrice : 0,
    preFillBuyerName: transaction?.counterparty_name ?? "",
    preFillBuyerCountry: transaction?.counterparty_country ?? "",
    preFillBuyerEmails: transaction?.counterparty_emails ?? "",
    preFillBuyerAddress: gc?.address ?? "",
    preFillBuyerRegistryCode: gc?.business_id ?? "",
    preFillBuyerVat: gc?.vat ?? "",
  };
}

export interface BuyerInvoiceLastSend {
  label: string;
  date: string;
  title: string;
}

/**
 * The most recent send shown under the buyer-invoice badge: the newest automatic reminder when
 * any went out, otherwise the original send (emailed or recorded manually). Nothing once the
 * invoice is paid — the paid badge is the whole story; the expanded row keeps the full history.
 */
export function getBuyerInvoiceLastSend(
  t: SalesTransaction
): BuyerInvoiceLastSend | null {
  if (t.buyer_sales_invoice_paid_at) {
    return null;
  }
  const reminders = t.buyer_sales_invoice_reminders_sent_at;
  if (reminders.length > 0) {
    return {
      label: `Reminder ${reminders.length} sent`,
      date: reminders[reminders.length - 1],
      title:
        "Date the latest automatic payment reminder was emailed to the buyer",
    };
  }
  if (t.buyer_sales_invoice_sent_at) {
    return {
      label: "Sent",
      date: t.buyer_sales_invoice_sent_at,
      title: t.buyer_sales_invoice_sent_manually
        ? "Date the invoice was sent to the buyer (recorded manually)"
        : "Date the invoice was emailed to the buyer",
    };
  }
  return null;
}
